import { Fragment } from 'react';
import { Menu } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { useMedia } from 'react-use';
import { BrazeCallout } from '@hungryroot/braze';
import { useExperimentFlags } from '@hungryroot/experiments';
import { Price } from '@hungryroot/pho';
import { useGetCustomerRewardCredits } from '@hungryroot/rest-hooks';
import {
  PLAN_ROUTE,
  REWARDS_ROUTE,
  WALLET_ROUTE,
  ACCOUNT_HISTORY_ROUTE,
  ACCOUNT_SETTINGS_ROUTE,
  ACCOUNT_HELP_CENTER_ROUTE,
  GIFT_ROUTE,
  COMMUNITY_URL,
  FOOD_PROFILE_ROUTE,
} from '@hungryroot/routes';
import { buttonClickedEvent } from '@hungryroot/segment';
import {
  Paragraph,
  Heading,
  Button,
  IconButton,
  Box,
  BreakpointQueryLarge,
  CaretDownNew,
  ChefHat,
  Facebook,
  Gear,
  List,
  Question,
  Sparkle,
  Wallet,
  X,
} from '@hungryroot/ui-kit';
import { ProfileRewardsWidget } from '../../../exp-245-rewards/components/ProfileRewardsWidget/ProfileRewardsWidget';
import { ProfileRewardsWidgetSkeleton } from '../../../exp-245-rewards/components/ProfileRewardsWidget/ProfileRewardsWidgetSkeleton';
import { useDocumentAwareNavigate } from '../../../hooks/useDocumentAwareNavigate';
import {
  useFoodProfileFilteredItems,
  AccountDropdownFoodProfile,
} from '../AccountDropDownFoodProfile/AccountDropdownFoodProfile';
import styles from './AccountDropdown.module.css';

interface Props {
  testid?: string;
  credit: string;
  customerFirstName?: string | undefined | null;
  isCanceled: boolean;
  onCloseDropdown?: () => void;
  isDropdownOpen?: boolean;
}

interface MenuItemProps {
  href: string;
  testid: string;
  text: string;
}

const originalItems = [
  {
    href: FOOD_PROFILE_ROUTE,
    testid: 'link-food-profile',
    text: 'Food Profile',
    hideIfCanceled: true,
    icon: <ChefHat />,
    isSpaRoute: false,
  },
  {
    href: ACCOUNT_HISTORY_ROUTE,
    testid: 'link-order-history',
    text: 'Order History',
    icon: <List />,
    isSpaRoute: true,
  },
  {
    href: WALLET_ROUTE,
    testid: 'link-wallet',
    text: 'Wallet',
    icon: <Wallet />,
    isSpaRoute: true,
  },
  {
    href: ACCOUNT_SETTINGS_ROUTE,
    testid: 'link-settings',
    text: 'Settings',
    icon: <Gear />,
    isSpaRoute: true,
  },
  {
    href: ACCOUNT_HELP_CENTER_ROUTE,
    testid: 'link-help-center',
    text: 'Help Center',
    icon: <Question />,
    isSpaRoute: false,
  },
  {
    href: GIFT_ROUTE,
    testid: 'link-gifts',
    text: 'Gifts',
    icon: <Sparkle />,
    isSpaRoute: false,
  },
  {
    href: COMMUNITY_URL,
    testid: 'link-community',
    text: 'Community',
    icon: <Facebook />,
    isSpaRoute: false,
  },
];

export function AccountDropdownContent(props: Props) {
  const {
    testid,
    credit,
    customerFirstName,
    isCanceled,
    onCloseDropdown,
    isDropdownOpen = true,
  } = props;
  const { navigate, forceDocumentNavigation } = useDocumentAwareNavigate();
  const isDesktop = useMedia(BreakpointQueryLarge);
  const {
    popsicleExp242,
    sprinklesExp245,
    isLoading: isExperimentsLoading,
  } = useExperimentFlags([242, 245]);
  const isInPopsicleVariant =
    popsicleExp242 !== undefined && popsicleExp242?.isVariant === true;
  const isInSprinklesVariant =
    !isExperimentsLoading && sprinklesExp245?.isVariant === true;
  const isMobilePopsicle = isInPopsicleVariant && !isDesktop;
  const { data: rewardCredits, isLoading: isRewardCreditsLoading } =
    useGetCustomerRewardCredits({ enabled: isInSprinklesVariant });
  const heading = customerFirstName ? `Hi, ${customerFirstName}!` : 'Profile';
  const accountBalance = parseFloat(credit);

  const filteredOriginalItems = useFoodProfileFilteredItems({
    originalItems,
  });

  const items = [
    {
      href: PLAN_ROUTE,
      testid: 'link-plan',
      text: 'Your Plan',
      hideIfCanceled: false,
      icon: <Box />,
      isSpaRoute: true,
    },
    ...filteredOriginalItems,
  ];

  const click = (item: MenuItemProps) => {
    buttonClickedEvent({
      order_id: null,
      button_name: item.text,
      page_position: 'Profile Menu',
      target_url_path: item.href.startsWith('http') ? undefined : item.href,
      target_url: item.href.startsWith('http') ? item.href : undefined,
    });
  };

  const renderMenuItems = () => {
    const getClassName = (text: string) => {
      if (text === 'Wallet') return styles['link-flex'];
      return styles['link'];
    };

    const getClassNameHover = (text: string) => {
      if (text === 'Wallet') return styles['link-hover-flex'];
      return styles['link-hover'];
    };

    const getTarget = (text: string) => {
      if (text === 'Help Center' || text === 'Community') return '_blank';
      return '';
    };

    return items.map((item) => {
      if (isCanceled && (item as { hideifCanceled?: boolean }).hideifCanceled) {
        return null;
      }

      const {
        text: itemText,
        href: itemHref,
        icon: itemIcon,
        isSpaRoute,
      } = item;

      const linkContent = (
        <div className={styles['item-container']}>
          <div className={styles['text-container']}>
            {itemIcon}
            <Paragraph variant="body">{itemText}</Paragraph>
          </div>

          <div className={styles.caret}>
            {accountBalance && itemHref === '/wallet/' ? (
              <Paragraph
                variant="body"
                testid={`${testid}-account-balance`}
                className={styles['menu-button-text-callout']}
              >
                <Price amount={accountBalance} />
              </Paragraph>
            ) : null}
            {!isDesktop && <CaretDownNew />}
          </div>
        </div>
      );

      return (
        <Fragment key={itemText}>
          {itemText === 'Help Center' ? (
            <hr className={styles['hr-line']} />
          ) : null}
          <Menu.Item>
            {({ active }) =>
              isSpaRoute && !forceDocumentNavigation ? (
                <Link
                  className={
                    active
                      ? getClassNameHover(itemText)
                      : getClassName(itemText)
                  }
                  to={itemHref}
                  data-testid={`${testid}-${item.testid}`}
                  onClick={() => click(item)}
                >
                  {linkContent}
                </Link>
              ) : (
                <a
                  className={
                    active
                      ? getClassNameHover(itemText)
                      : getClassName(itemText)
                  }
                  href={itemHref}
                  data-testid={`${testid}-${item.testid}`}
                  target={getTarget(itemText)}
                  onClick={() => click(item)}
                >
                  {linkContent}
                </a>
              )
            }
          </Menu.Item>
        </Fragment>
      );
    });
  };

  return (
    <>
      {isDesktop ? null : isMobilePopsicle ? (
        <div className={styles['heading-row']}>
          <span className={styles['heading-spacer']} aria-hidden="true" />
          <Heading
            variant="H4"
            className={styles.heading}
            testid={`${testid}-heading`}
          >
            {heading}
          </Heading>
          <IconButton
            ariaLabel="Close profile menu"
            style="default"
            prominence="tertiary"
            size="small"
            buttonType="button"
            icon={<X />}
            onClick={onCloseDropdown}
            testid={`${testid}-close`}
            className={styles['heading-close']}
          />
        </div>
      ) : (
        <Heading
          variant="H4"
          className={styles.heading}
          testid={`${testid}-heading`}
        >
          {heading}
        </Heading>
      )}
      <div className={styles.braze}>
        <BrazeCallout location="profile_menu_top" />
      </div>
      {isInSprinklesVariant ? (
        isRewardCreditsLoading ? (
          <ProfileRewardsWidgetSkeleton
            isMobilePopsicle={isMobilePopsicle}
            testId={`${testid}-rewards-widget-skeleton`}
          />
        ) : (rewardCredits?.milestones?.length ?? 0) > 0 ? (
          <ProfileRewardsWidget
            creditsSpent={rewardCredits?.lifetime_earned_credits ?? 0}
            lifetimeSpentCredits={rewardCredits?.lifetime_spent_credits ?? 0}
            lifetimeRewardCredits={rewardCredits?.lifetime_reward_credits ?? 0}
            milestones={rewardCredits?.milestones ?? []}
            isMobilePopsicle={isMobilePopsicle}
            onSeeYourRewards={() => {
              navigate(REWARDS_ROUTE);
            }}
            onCloseDropdown={onCloseDropdown}
            trackingEnabled={isDropdownOpen}
            testId={`${testid}-rewards-widget`}
          />
        ) : null
      ) : null}
      <AccountDropdownFoodProfile testid={testid} />
      {renderMenuItems()}
      <div className={styles['button']}>
        <Button
          style="muted"
          prominence="secondary"
          size="medium"
          width="full-width-mobile"
          testid={`${testid}-link-logout`}
          buttonType="link"
          to="/logout/"
        >
          Sign out
        </Button>
      </div>
    </>
  );
}
